import { useTheme } from '@material-ui/core/styles'
import classNames from 'classnames'
import { orgSelectors } from 'ducks/orgs'
import { Box, Tooltip, UserProfileGroupIcon, UserProfileGroupOutlineIcon } from 'opensolar-ui'

import { useForm } from 'react-final-form'
import { useSelector } from 'react-redux'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import useCommonStyles from './useCommonStyles'

const useStyles = makeOpenSolarStyles(() => ({
  nameContainer: {
    display: 'flex',
    rowGap: '18px',
  },
  helperIcon: {
    marginLeft: '10px',
    '& > div': {
      display: 'flex',
    },
  },
  seriesText: {
    display: '-webkit-box',
    lineClamp: 2,
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  },
  tooltipContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    minWidth: 0,
    overflow: 'hidden',
  },
  blackTooltip: {
    backgroundColor: '#000000 !important',
    border: '1px solid #000000 !important',
    '& *': {
      color: '#FFFFFF !important',
    },
    '& [class*="OSUI-Tooltip-arrow"]': {
      color: '#000000 !important',
    },
    '& [class*="OSUI-Tooltip-arrow"]::before': {
      backgroundColor: '#000000 !important',
    },
  },
}))

interface NameBlockProps {
  code?: string
  name?: string
  series?: string
  helperIcon?: React.ReactNode
  component?: any
}

const NameBlock: React.FC<NameBlockProps> = ({ code, name, series, helperIcon, component }) => {
  const commonClasses = useCommonStyles()
  const classes = useStyles()
  const theme = useTheme()

  // Get form values directly
  const form = useForm()
  const formValues = form.getState().values

  // Get current organization ID directly
  const currentOrgId = useSelector(orgSelectors.getOrg)?.id

  // Get first shared organization
  const getFirstSharedOrg = () => {
    if (!formValues?.shared_with || formValues.shared_with.length === 0) {
      return null
    }

    const firstSharedOrg = formValues.shared_with[0]
    if (!firstSharedOrg) {
      return null
    }

    return firstSharedOrg
  }

  // Get shared organization name
  const getSharedOrgName = () => {
    const firstSharedOrg = getFirstSharedOrg()
    if (!firstSharedOrg) {
      return null
    }

    let sharedOrgName = firstSharedOrg.org_name
    const projectOwnerId = formValues.org_id

    // If current org is the same as project owner, show firstSharedOrg.org_name
    // Otherwise show project owner org name as shared from
    if (currentOrgId === projectOwnerId) {
      // Current org is project owner, use firstSharedOrg.org_name
      sharedOrgName = firstSharedOrg.org_name
    } else {
      // Current org is not project owner, use project owner org name from formValues
      sharedOrgName = formValues.org_name
    }

    return sharedOrgName
  }

  // Determine sharing status
  const getSharingProps = () => {
    const sharedOrgName = getSharedOrgName()
    if (!sharedOrgName) {
      return { sharedFrom: false, sharedWith: false, orgName: undefined }
    }

    const firstSharedOrg = getFirstSharedOrg()
    if (!firstSharedOrg) {
      return { sharedFrom: false, sharedWith: false, orgName: undefined }
    }

    // Check if component.share_with_org_ids is not null or empty
    const hasShareWithOrgIds = component?.share_with_org_ids && component.share_with_org_ids.length > 0

    // Extra check: component.share_with_org_ids must include firstSharedOrg.org_id or currentOrgId
    const includesRelevantOrgId =
      hasShareWithOrgIds &&
      (component.share_with_org_ids.includes(firstSharedOrg.org_id) ||
        component.share_with_org_ids.includes(currentOrgId))

    if (component?.org_id && component.org_id === currentOrgId && includesRelevantOrgId) {
      // Component is from current org, so it's shared with another org
      return { sharedFrom: false, sharedWith: true, orgName: sharedOrgName }
    } else if (component?.org_id && component.org_id !== currentOrgId && includesRelevantOrgId) {
      // Component is from another org, so it's shared from that org
      return { sharedFrom: true, sharedWith: false, orgName: sharedOrgName }
    }

    return { sharedFrom: false, sharedWith: false, orgName: undefined }
  }

  const { sharedFrom, sharedWith, orgName } = getSharingProps()

  return (
    <Box display="flex" className={commonClasses.textsContainer} flexDirection="column">
      <Box alignItems="center" display="flex" className={classes.nameContainer}>
        {sharedFrom || sharedWith ? (
          <Tooltip
            title={sharedWith ? `Shared with ${orgName}` : `Shared from ${orgName}`}
            classes={{
              tooltip: classes.blackTooltip,
            }}
          >
            <div className={classes.tooltipContent}>
              {name ? (
                <>
                  <span className={commonClasses.mainText}>{name}</span>
                  <span className={commonClasses.lightText}>{code}</span>
                </>
              ) : (
                <span className={commonClasses.mainText}>{code}</span>
              )}
              {sharedFrom && <UserProfileGroupOutlineIcon size={16} color={theme.palette.grey[400]} />}
              {sharedWith && <UserProfileGroupIcon size={16} color={theme.palette.grey[400]} />}
            </div>
          </Tooltip>
        ) : (
          <div className={classes.tooltipContent}>
            {name ? (
              <>
                <span className={commonClasses.mainText}>{name}</span>
                <span className={commonClasses.lightText}>{code}</span>
              </>
            ) : (
              <span className={commonClasses.mainText}>{code}</span>
            )}
          </div>
        )}
        {!!helperIcon && !sharedFrom && !sharedWith && <span className={classes.helperIcon}>{helperIcon}</span>}
      </Box>
      {series && (
        <span
          className={classNames([commonClasses.subText, classes.seriesText])}
          onMouseEnter={({ currentTarget: target }) => {
            const currentTitle = target.getAttribute('title')
            if (target.offsetHeight < target.scrollHeight && !currentTitle) {
              target.setAttribute('title', series)
            }
          }}
        >
          {series}
        </span>
      )}
    </Box>
  )
}

export default NameBlock
